import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { ListSkeleton } from '@atlassian/jira-skeletons/src/ui/list/ListSkeleton.tsx';
import { composeLayouts, createLayout } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { classicSoftwareProjectLayout } from '@atlassian/jira-software-project-layout/src/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@atlassian/jira-issue-preload-layout/src/index.tsx';
import { MENU_ID, SIDEBAR_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import AsyncSidebarSoftwareClassic, {
	ForPaintClassicSoftwareProjectSidebar as AsyncForPaintSidebarSoftwareClassic,
} from '@atlassian/jira-navigation-apps-sidebar-software-classic/src/async.tsx';
import { softwareListClassicRoute } from '@atlassian/jira-router-routes-classic-projects-routes/src/softwareListClassicRoute.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { projectCreatePermissionsResource } from '@atlassian/jira-router-resources-project-permissions/src/services/project-permissions/index.tsx';
import { navigationResource } from '@atlassian/jira-router-resources-business-navigation/src/index.tsx';
// eslint-disable-next-line camelcase
import { DEPRECATED_DO_NOT_USE_projectDetailsResource } from '@atlassian/jira-router-resources-business-project-details/src/index.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { forgeProjectModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { fieldsResource } from '@atlassian/jira-router-resources-business-fields/src/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { projectThemeResource } from '@atlassian/jira-router-resources-project-theme/src/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@atlassian/jira-capabilities/src/constants.tsx';
import { getRouteReporting } from '@atlassian/jira-router-routes-issue-navigator-entries/src/common/getRouteReporting.tsx';
import { ProjectIssueNavigatorSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { issueNavigatorPageEntryPointWithNested } from '@atlassian/jira-router-routes-issue-navigator-entries/src/ui/index.tsx';

import { genericProjectLayout } from '@atlassian/jira-generic-project-layout/src/index.tsx';

import { getCommonProjectResources } from '@atlassian/jira-router-routes-issue-navigator-entries/src/common/getCommonProjectResources.tsx';
import { softwareListRouteEntry } from './ui/software/list/index.tsx';

export const softwareListClassicRouteEntry = createEntry(softwareListClassicRoute, () => ({
	group: 'classic-software',
	meta: fg('jira_list_nin_merge')
		? {
				capability: NIN_NEW_ISSUE_NAVIGATOR,
				reporting: getRouteReporting(),
			}
		: undefined,
	skeleton: fg('jira_list_nin_merge') ? ProjectIssueNavigatorSkeleton : ListSkeleton,
	entryPoint() {
		if (fg('jira_list_nin_merge')) {
			return issueNavigatorPageEntryPointWithNested;
		}
		return softwareListRouteEntry;
	},
	layout: fg('jira_list_nin_merge')
		? createLayout(genericProjectLayout)
		: composeLayouts(
				classicSoftwareProjectLayout,
				embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }]),
			),
	bifrost: {
		bifrostGroup: 'issue-navigator',
		bundling: {
			packageName:
				'@atlassian/jira-router-routes-classic-projects-entries/src/softwareListClassicRouteEntry.tsx',
			whichExportToUse: 'softwareListClassicRouteEntry',
		},
		enabled: true,
		manifestName: 'jira-spa-issue-navigator.json',
		// An increase from 1.2s default to 1.5s will comfortably cater for P95 of issue search requests
		// For more insights into this decision, see https://jplat.atlassian.net/browse/EM-9249
		fetchTimeout: fg('increase_server_fetch_timeouts_for_nin_bifrost') ? 1500 : undefined,
	},
	navigation: fg('jira_list_nin_merge')
		? {
				menuId: MENU_ID.PROJECTS,
				sidebar: AsyncForPaintSidebarSoftwareClassic,
				horizontal: AsyncHorizontalNavJira,
				onlyShowHorizontalOnNav4: true,
				sidebarId: SIDEBAR_ID.CONTENT,
			}
		: {
				menuId: MENU_ID.PROJECTS,
				sidebar: AsyncSidebarSoftwareClassic,
				horizontal: AsyncHorizontalNavJira,
				onlyShowHorizontalOnNav4: true,
				sidebarId: SIDEBAR_ID.CONTENT,
			},
	// TODO: This is a copy of getProjectViewResources, but it has been copied in order to replace
	//  with .
	resources: fg('jira_list_nin_merge')
		? [
				...getCommonProjectResources({
					isUsingSidebarEntrypoint: true,
				}),
			]
		: [
				// the below 5 resources are copied from getBusinessProjectResources
				// they should be feature flagged and removed when it is safe to do so or
				// moved out of business packages
				// start getBusinessProjectResources
				projectCreatePermissionsResource,
				navigationResource,
				// eslint-disable-next-line camelcase
				DEPRECATED_DO_NOT_USE_projectDetailsResource,
				// end getBusinessProjectResources
				...getNavigationResources({
					isUsingSidebarEntrypoint: true,
				}),
				// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
				incidentExperimentTraitResource,
				projectContextResource,
				forgeProjectModuleResource,
				staticSidebarShortcutsResource,
				staticProjectSidebarNavigationResource,
				staticProjectSidebarConnectAddonsNavigationResource,
				// end of resources expanded from getNavigationSidebarProjectResource
				fieldsResource,
				resourceWithCondition2(() => getIsSoftwareThemingEnabled(), projectThemeResource),
			],
	afterPaint: fg('jira_list_nin_merge') ? undefined : [LazyIssueApp],
	ufoName: fg('jira_list_nin_merge') ? 'classic-list-nin' : 'classic-list',
	supportsPreviewPanel: true,
	isHydrateRootEnabled: fg('jira_list_nin_merge') ? fg('nin-full-page-hydrate-root') : false,
}));
